/*
  Copyright (C) 2022-2025 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { defineStore } from "pinia";
import { useConfigStore } from "./config";
import rocketChatService from "@/services/RocketChatService";
import rocketChatAuthService from "@/services/RocketChatAuthService";
import chatBackendService from "@/services/ChatBackendService";
import {
  useChatWebSocket,
  ConnectionState,
} from "@/composables/useChatWebSocket";

export const useChatStore = defineStore({
  id: "chat",

  state: () => ({
    // Feature availability
    enabled: false,
    loaded: false,
    hasCredentials: false,

    // Connection state
    connectionState: ConnectionState.DISCONNECTED,
    lastError: null,

    // Rooms
    rooms: [],
    currentRoomId: null,

    // Messages (roomId -> messages array)
    messagesByRoom: {},

    // Unread counts (roomId -> count)
    unreadCounts: {},

    // Typing indicators (roomId -> [usernames])
    typingByRoom: {},

    // Current user info
    currentUser: null,

    // WebSocket instance
    _webSocket: null,
  }),

  getters: {
    /**
     * Returns the current room object
     */
    currentRoom: (state) => {
      if (!state.currentRoomId) {
        return null;
      }
      return state.rooms.find((r) => r._id === state.currentRoomId) || null;
    },

    /**
     * Returns messages for the current room
     */
    currentMessages: (state) => {
      if (!state.currentRoomId) {
        return [];
      }
      return state.messagesByRoom[state.currentRoomId] || [];
    },

    /**
     * Returns typing users for the current room
     */
    currentTypingUsers: (state) => {
      if (!state.currentRoomId) {
        return [];
      }
      return state.typingByRoom[state.currentRoomId] || [];
    },

    /**
     * Returns the total unread count across all rooms
     */
    totalUnreadCount: (state) => {
      return Object.values(state.unreadCounts).reduce(
        (sum, count) => sum + count,
        0,
      );
    },

    /**
     * Returns true if chat is available and connected
     */
    isReady: (state) =>
      state.enabled &&
      state.hasCredentials &&
      state.connectionState === ConnectionState.READY,

    /**
     * Returns true if currently connecting
     */
    isConnecting: (state) =>
      state.connectionState === ConnectionState.CONNECTING ||
      state.connectionState === ConnectionState.AUTHENTICATING,

    /**
     * Returns true if reconnecting
     */
    isReconnecting: (state) =>
      state.connectionState === ConnectionState.RECONNECTING,

    /**
     * Returns true if chat feature is available for use
     */
    isAvailable: (state) =>
      state.enabled && state.loaded && state.hasCredentials,
  },

  actions: {
    /**
     * Initializes the chat store.
     * Checks if the feature is enabled and credentials are available.
     */
    async init() {
      try {
        const config = useConfigStore();

        // Initialize auth service with config
        rocketChatAuthService.init({
          CHAT_ENABLED: config.CHAT_ENABLED,
          ROCKETCHAT_HOST: config.ROCKETCHAT_HOST,
          ROCKETCHAT_WS_URL: config.ROCKETCHAT_WS_URL,
          CHAT_OAUTH_ENABLED: config.CHAT_OAUTH_ENABLED,
          CHAT_OAUTH_SERVICE: config.CHAT_OAUTH_SERVICE,
        });

        this.enabled = rocketChatAuthService.isEnabled();

        if (this.enabled) {
          // Check if user has credentials configured
          const creds = await rocketChatAuthService.getCredentials();
          this.hasCredentials = !!creds;

          if (this.hasCredentials) {
            // Get current user info
            this.currentUser = await rocketChatAuthService.getCurrentUser();
          }
        }

        this.loaded = true;
        console.log(
          "[ChatStore] Initialized, enabled:",
          this.enabled,
          "hasCredentials:",
          this.hasCredentials,
        );
      } catch (error) {
        console.error("[ChatStore] Failed to initialize:", error);
        this.enabled = false;
        this.loaded = true;
      }
    },

    /**
     * Connects to the Rocket.Chat WebSocket server.
     */
    async connect() {
      if (!this.enabled || !this.hasCredentials) {
        console.warn("[ChatStore] Cannot connect: chat not available");
        return false;
      }

      if (this._webSocket && this.connectionState === ConnectionState.READY) {
        return true;
      }

      try {
        // Create WebSocket instance
        this._webSocket = useChatWebSocket();

        // Set up event handlers
        this._webSocket.on("onReady", () => {
          this.connectionState = ConnectionState.READY;
          this.loadRooms();

          // Subscribe to current room if already selected (handles race condition)
          if (this.currentRoomId) {
            this._webSocket.subscribeRoom(this.currentRoomId);
          }
        });

        this._webSocket.on("onDisconnected", () => {
          this.connectionState = ConnectionState.DISCONNECTED;
        });

        this._webSocket.on("onReconnecting", (attempt) => {
          this.connectionState = ConnectionState.RECONNECTING;
          console.log("[ChatStore] Reconnecting, attempt:", attempt);
        });

        this._webSocket.on("onError", (error) => {
          this.lastError = error;
          this.connectionState = ConnectionState.ERROR;
        });

        this._webSocket.on("onRoomMessage", (message) => {
          this.handleNewMessage(message);
        });

        this._webSocket.on("onTyping", (username, isTyping) => {
          this.handleTypingIndicator(username, isTyping);
        });

        // Connect
        this.connectionState = ConnectionState.CONNECTING;
        await this._webSocket.connect();

        return true;
      } catch (error) {
        console.error("[ChatStore] Connection failed:", error);
        this.lastError = error;
        this.connectionState = ConnectionState.ERROR;
        return false;
      }
    },

    /**
     * Disconnects from the WebSocket server.
     */
    disconnect() {
      if (this._webSocket) {
        this._webSocket.disconnect();
        this._webSocket = null;
      }
      this.connectionState = ConnectionState.DISCONNECTED;
    },

    /**
     * Loads the user's subscribed rooms.
     */
    async loadRooms() {
      try {
        const subscriptions = await rocketChatService.getSubscriptions();

        // Transform subscriptions to room format
        this.rooms = subscriptions.map((sub) => ({
          _id: sub.rid,
          name: sub.name,
          fname: sub.fname || sub.name,
          type: sub.t,
          unread: sub.unread || 0,
          lastMessage: sub.lastMessage,
          updatedAt: sub._updatedAt,
        }));

        // Update unread counts
        const counts = {};
        subscriptions.forEach((sub) => {
          if (sub.unread > 0) {
            counts[sub.rid] = sub.unread;
          }
        });
        this.unreadCounts = counts;
      } catch (error) {
        console.error("[ChatStore] Failed to load rooms:", error);
      }
    },

    /**
     * Selects a room and loads its messages.
     *
     * @param {string} roomId - The room ID to select
     */
    async selectRoom(roomId) {
      if (!roomId) {
        this.currentRoomId = null;
        return;
      }

      this.currentRoomId = roomId;

      // Subscribe to room if WebSocket is ready
      if (this._webSocket && this._webSocket.isReady()) {
        await this._webSocket.subscribeRoom(roomId);
      }

      // Load messages if not already loaded
      if (!this.messagesByRoom[roomId]) {
        await this.loadMessages(roomId);
      }

      // Mark as read
      await this.markAsRead(roomId);
    },

    /**
     * Loads messages for a room.
     *
     * @param {string} roomId - The room ID
     * @param {Object} options - Load options
     */
    async loadMessages(roomId, options = {}) {
      try {
        const messages = await rocketChatService.getMessages(roomId, {
          count: options.count || 50,
          oldest: options.oldest,
          latest: options.latest,
        });

        // Messages come in reverse chronological order, reverse them
        const sortedMessages = [...messages].reverse();

        if (options.prepend && this.messagesByRoom[roomId]) {
          // Prepend older messages
          this.messagesByRoom[roomId] = [
            ...sortedMessages,
            ...this.messagesByRoom[roomId],
          ];
        } else {
          this.messagesByRoom[roomId] = sortedMessages;
        }
      } catch (error) {
        console.error("[ChatStore] Failed to load messages:", error);
      }
    },

    /**
     * Sends a message to the current room.
     *
     * @param {string} text - The message text
     */
    async sendMessage(text) {
      if (!this.currentRoomId || !text.trim()) {
        return;
      }

      try {
        if (this._webSocket && this._webSocket.isReady()) {
          await this._webSocket.sendChatMessage(this.currentRoomId, text);
        } else {
          // Fallback to REST API
          await rocketChatService.sendMessage(this.currentRoomId, text);
        }
      } catch (error) {
        console.error("[ChatStore] Failed to send message:", error);
        throw error;
      }
    },

    /**
     * Marks a room as read.
     *
     * @param {string} roomId - The room ID
     */
    async markAsRead(roomId) {
      try {
        await rocketChatService.markAsRead(roomId);
        this.unreadCounts[roomId] = 0;
      } catch (error) {
        console.error("[ChatStore] Failed to mark as read:", error);
      }
    },

    /**
     * Handles a new incoming message.
     *
     * @param {Object} message - The message object
     */
    handleNewMessage(message) {
      const roomId = message.rid;

      // Initialize room messages array if needed
      if (!this.messagesByRoom[roomId]) {
        this.messagesByRoom[roomId] = [];
      }

      // Normalize timestamp from Meteor format {$date: timestamp} to ISO string
      if (message.ts && typeof message.ts === "object" && message.ts.$date) {
        message.ts = new Date(message.ts.$date).toISOString();
      }

      // Check if message already exists
      const exists = this.messagesByRoom[roomId].some(
        (m) => m._id === message._id,
      );
      if (!exists) {
        this.messagesByRoom[roomId].push(message);

        // Update unread count if not current room
        if (roomId !== this.currentRoomId) {
          this.unreadCounts[roomId] = (this.unreadCounts[roomId] || 0) + 1;
        }
      }
    },

    /**
     * Handles typing indicator updates.
     *
     * @param {string} username - The username
     * @param {boolean} isTyping - Whether the user is typing
     */
    handleTypingIndicator(username, isTyping) {
      const roomId = this.currentRoomId;
      if (!roomId) {
        return;
      }

      // Skip if it's the current user
      if (this.currentUser && username === this.currentUser.username) {
        return;
      }

      if (!this.typingByRoom[roomId]) {
        this.typingByRoom[roomId] = [];
      }

      if (isTyping) {
        if (!this.typingByRoom[roomId].includes(username)) {
          this.typingByRoom[roomId].push(username);
        }
      } else {
        this.typingByRoom[roomId] = this.typingByRoom[roomId].filter(
          (u) => u !== username,
        );
      }
    },

    /**
     * Sends typing indicator.
     *
     * @param {boolean} isTyping - Whether the user is typing
     */
    sendTypingIndicator(isTyping) {
      if (
        !this.currentRoomId ||
        !this.currentUser ||
        !this._webSocket ||
        !this._webSocket.isReady()
      ) {
        return;
      }

      this._webSocket.sendTyping(
        this.currentRoomId,
        this.currentUser.username,
        isTyping,
      );
    },

    /**
     * Gets or creates a room for a workspace.
     *
     * @param {string} siteId - The Alfresco site ID
     * @returns {Promise<Object|null>} Room info or null
     */
    async getOrCreateWorkspaceRoom(siteId) {
      try {
        // First, try to get existing room
        let roomInfo = await chatBackendService.getWorkspaceRoom(siteId);

        if (!roomInfo.found) {
          // Create the room via backend
          roomInfo = await chatBackendService.createWorkspaceRoom(siteId);
        }

        if (roomInfo && roomInfo.roomId) {
          // Refresh rooms list to include the new room
          await this.loadRooms();
          return roomInfo;
        }

        return null;
      } catch (error) {
        console.error(
          "[ChatStore] Failed to get/create workspace room:",
          error,
        );
        return null;
      }
    },

    /**
     * Uploads a file to the current room.
     *
     * @param {File} file - The file to upload
     * @param {string} description - Optional description
     */
    async uploadFile(file, description = "") {
      if (!this.currentRoomId) {
        throw new Error("No room selected");
      }

      try {
        await rocketChatService.uploadFile(
          this.currentRoomId,
          file,
          description,
        );
      } catch (error) {
        console.error("[ChatStore] Failed to upload file:", error);
        throw error;
      }
    },

    /**
     * Clears all chat data.
     */
    clear() {
      this.disconnect();
      this.rooms = [];
      this.currentRoomId = null;
      this.messagesByRoom = {};
      this.unreadCounts = {};
      this.typingByRoom = {};
      this.currentUser = null;
      this.lastError = null;
    },
  },
});
